import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

/**
 * CTable - A reusable table component with built-in pagination and search
 * 
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Array of data objects
 * @param {string} props.title - Table title
 * @param {boolean} props.loading - Whether data is loading
 * @param {Object} props.tableProps - Additional props for the Table component
 * @param {Object} props.sx - Additional styling
 * @param {string} props.searchPlaceholder - Placeholder text for search field
 * @param {Function} props.onSearchChange - Function to handle search change
 * @param {string} props.searchValue - Search value
 * @param {boolean} props.showSearch - Whether to show search field
 * @param {number} props.defaultRowsPerPage - Default rows per page
 * @param {Array} props.rowsPerPageOptions - Options for rows per page
 * @param {Function} props.onPageChange - Function to handle page change
 * @param {Function} props.onRowsPerPageChange - Function to handle rows per page change
 * @param {number} props.page - Current page
 * @param {number} props.rowsPerPage - Rows per page
 * @param {string} props.emptyMessage - Message to show when no data is available
 * @param {boolean} props.autoFit - Whether to auto-fit columns (default: true)
 * @returns {JSX.Element}
 */
const CTable = ({
  columns = [],
  data = [],
  title,
  loading = false,
  tableProps = {},
  sx = {},
  searchPlaceholder = "Search",
  onSearchChange,
  searchValue = "",
  showSearch = true,
  defaultRowsPerPage = 25,
  rowsPerPageOptions = [5, 10, 25, 50, 100],
  onPageChange,
  onRowsPerPageChange,
  page = 0,
  rowsPerPage = defaultRowsPerPage,
  emptyMessage = "No records found",
  autoFit = true
}) => {
  // Internal state for pagination if not controlled externally
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(defaultRowsPerPage);
  const [internalSearchValue, setInternalSearchValue] = useState("");
  
  // Use either controlled or internal state
  const currentPage = onPageChange ? page : internalPage;
  const currentRowsPerPage = onRowsPerPageChange ? rowsPerPage : internalRowsPerPage;
  const currentSearchValue = onSearchChange ? searchValue : internalSearchValue;

  // Simple consistent styling for all tables
  const getTableStyles = () => {
    return {
      // Force override MUI styles - default small font for all zoom levels
      '& .MuiTableCell-root': {
        fontSize: '0.7rem !important',
        padding: '6px 8px !important',
        lineHeight: '1.3 !important',
        verticalAlign: 'middle',
      },
      '& .MuiTableCell-head': {
        fontSize: '0.6rem !important',
        fontWeight: '500 !important',
      },
      '& .MuiTableCell-body': {
        fontSize: '0.7rem !important',
      },
      // Only at 100%, 110%, and 125% zoom make data cells bigger
      '@media (resolution: 1dppx), (resolution: 1.1dppx), (resolution: 1.25dppx)': {
        '& .MuiTableCell-body': {
          fontSize: '1rem !important',
        }
      }
    };
  };

  // Handle page change (internal or external)
  const handleChangePage = (event, newPage) => {
    if (onPageChange) {
      onPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  // Handle rows per page change (internal or external)
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalPage(0); // Reset to first page
    }
  };

  // Handle search change (internal or external)
  const handleSearchChange = (event) => {
    const newValue = event.target.value;
    if (onSearchChange) {
      onSearchChange(event);
    } else {
      setInternalSearchValue(newValue);
      setInternalPage(0); // Reset to first page on search
    }
  };

  // Filter data based on search term if not controlled externally
  const displayData = onSearchChange
    ? data
    : data.filter((item) => {
        if (!currentSearchValue) return true;
        
        // Search in all string or number fields
        return Object.values(item).some(value => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(currentSearchValue.toLowerCase());
          }
          if (typeof value === 'number') {
            return value.toString().includes(currentSearchValue);
          }
          return false;
        });
      });

  // Pagination logic - either controlled or internal
  const paginatedData = displayData.slice(
    currentPage * currentRowsPerPage,
    currentPage * currentRowsPerPage + currentRowsPerPage
  );

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {/* Header with title and search */}
      {(title || showSearch) && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          {title && (
            <Typography variant="body2" component="div" sx={{ fontSize: '0.7rem', fontWeight: 400, color: 'text.secondary' }}>
              {title}
            </Typography>
          )}
          
          {showSearch && (
            <TextField
              placeholder={searchPlaceholder}
              variant="outlined"
              size="small"
              value={currentSearchValue}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '1rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                minWidth: 250,
                '& .MuiInputBase-input': {
                  fontSize: '0.8rem'
                }
              }}
            />
          )}
        </Box>
      )}

      {/* Table content */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 'none',
          width: '100%',
          overflow: 'auto', // Allow minimal scroll if absolutely necessary
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '2px',
          }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
                      <Table 
            aria-label={title || "data table"} 
            size="small" 
            {...tableProps}
            sx={{
              tableLayout: autoFit ? 'auto' : 'auto',
              width: '100%',
              minWidth: autoFit ? '100%' : 'auto',
              // Apply consistent font styling
              ...getTableStyles(),
              ...tableProps.sx
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell 
                    key={index}
                    align={column.align || 'left'}
                    sx={{
                      whiteSpace: autoFit ? 'normal' : 'normal',
                      width: autoFit ? 'auto' : (column.minWidth || 'auto'),
                      minWidth: autoFit ? '80px' : (column.minWidth || 'auto'),
                      maxWidth: autoFit ? 'none' : 'none',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      fontSize: '0.8rem !important',
                      fontWeight: '500 !important',
                      wordBreak: autoFit ? 'break-word' : 'normal',
                      hyphens: autoFit ? 'auto' : 'none',
                      ...(column.sx || {})
                    }}
                  >
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, rowIndex) => (
                  <TableRow key={row.id || rowIndex} hover>
                    {columns.map((column, colIndex) => {
                      const cellValue = column.render ? column.render(row) : row[column.field];
                      const cellText = typeof cellValue === 'string' || typeof cellValue === 'number' 
                        ? String(cellValue) 
                        : '';
                      
                      return (
                        <TableCell 
                          key={colIndex}
                          align={column.align || 'left'}
                          sx={{ 
                            whiteSpace: autoFit ? 'normal' : 'normal',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            maxWidth: 'none',
                            wordBreak: autoFit ? 'break-word' : 'normal',
                            hyphens: autoFit ? 'auto' : 'none',
                            fontSize: '0.7rem !important', // Default small size
                            '@media (resolution: 1dppx), (resolution: 1.1dppx), (resolution: 1.25dppx)': {
                              fontSize: '1rem !important' // Bigger at 100%, 110%, and 125% zoom
                            },
                            ...(column.cellSx || {})
                          }}
                        >
                          {cellValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination controls - MADE SMALLER */}
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={displayData.length}
        rowsPerPage={currentRowsPerPage}
        page={currentPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          '& .MuiTablePagination-toolbar': {
            fontSize: '0.75rem'
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.75rem'
          },
          '& .MuiSelect-select': {
            fontSize: '0.75rem'
          }
        }}
      />
    </Box>
  );
};

CTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    header: PropTypes.node,
    align: PropTypes.string,
    minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sx: PropTypes.object,
    cellSx: PropTypes.object,
    render: PropTypes.func
  })),
  data: PropTypes.array.isRequired,
  title: PropTypes.node,
  loading: PropTypes.bool,
  tableProps: PropTypes.object,
  sx: PropTypes.object,
  searchPlaceholder: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchValue: PropTypes.string,
  showSearch: PropTypes.bool,
  defaultRowsPerPage: PropTypes.number,
  rowsPerPageOptions: PropTypes.array,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  emptyMessage: PropTypes.string,
  autoFit: PropTypes.bool
};

export default CTable;